import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { toTemporalZonedDateTime, formatDate } from "../../../shared/lib/temporal";
import { Video, VideoWithDevice, VideoPlayerHandler } from "../../../types/Video";
import useDownload from "./useDownload";
import { VideoMetadata, ScaledDimensions } from "../types/VideoTypes";
import { useQuery } from "@tanstack/react-query";
import { useFetchTrackedObjects } from "../../../hooks/useFetchTrackedObjects";
import { useApiClient } from "../../../providers/ApiClientProvider";
import { DirectionType } from "../../../types/Direction";
import { NamedSite } from "../../../types/Site";
import { createUrlWithQuery } from "../../../hooks/utils";
import { useUserContext } from "../../../providers/useUserProvider";

export interface DeviceConfigs {
    directions: DirectionType[];
    directionality: string;
    mode: "VIDEO_ONLY" | "COUNTS_ENABLED";
}

interface UseVideoControllerProps {
    readonly video: VideoWithDevice;
    readonly site: NamedSite;
}

export const useVideoController = ({ video, site }: UseVideoControllerProps) => {
    const videoRef = useRef<VideoPlayerHandler>(null);
    const videoElementRef = useRef<HTMLVideoElement>(null);
    const [currentVideoTime, setCurrentVideoTime] = useState<number>(0);
    const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null);
    const [scaledDimensions, setScaledDimensions] = useState<ScaledDimensions | null>(null);
    const [tableMode, setTableMode] = useState<"all" | "current">("all");
    const [isPlaying, setIsPlaying] = useState(false);
    const [displayBoundingBoxes, toggleDisplayBoundingBoxes] = useState(false);

    const timestamp = new Date(video.time).toISOString();

    const deviceConfig = useMemo(() => {
        if (!video?.device?.configs?.length || !video.time) return undefined;
        const videoTime = new Date(video.time).getTime();
        const config = video.device.configs.find((cfg) => {
            const from = cfg.effectiveFrom ? new Date(cfg.effectiveFrom).getTime() : -Infinity;
            const to = cfg.effectiveTo ? new Date(cfg.effectiveTo).getTime() : Infinity;
            return videoTime >= from && videoTime <= to && cfg.mode === "COUNTS_ENABLED";
        });
        if (config === undefined) return undefined;
        return {
            directionality: config.directionality,
            mode: config.mode,
            directions: Object.keys(config.directions ?? {}).map(
                (key) => DirectionType[key as keyof typeof DirectionType],
            ),
        };
    }, [video]);

    const {
        video: videoBlob,
        isLoading: isVideoLoading,
        error: downloadError,
    } = useDownload(video.device.id, timestamp, true);

    const apiClient = useApiClient();

    const { data: videoId } = useQuery({
        queryKey: ["videos", video.device.id, video.time],
        queryFn: async ({ signal }): Promise<Video[]> => {
            const response = await apiClient.get<{ data: Video[] }>(
                createUrlWithQuery(`/devices/${video.device.id}/videos`, {
                    time: timestamp,
                }),
                { signal },
            );
            return response.data;
        },
        select: (data: Video[]): string | null => {
            return data.length > 0 ? data[0].id : null;
        },
    });

    const {
        data: objectsData = [],
        isLoading: objectsLoading,
        isPending: objectsPending,
        error: objectsError,
    } = useFetchTrackedObjects(videoId, {
        latestOnly: true,
        directionIds: deviceConfig?.directions,
    });

    const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);

    // Manage blob URL creation and cleanup
    useEffect(() => {
        if (videoBlob && !isVideoLoading) {
            const url = URL.createObjectURL(videoBlob as Blob);
            setVideoBlobUrl(url);

            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setVideoBlobUrl(null);
        }
    }, [videoBlob, isVideoLoading]);

    // Add ordinal numbers to objects for display
    const objectsWithOrdinal = useMemo(() => {
        return [...objectsData]
            .sort((a, b) => new Date(a.firstSeen).getTime() - new Date(b.firstSeen).getTime())
            .map((obj, index) => ({
                ...obj,
                ordinal: index + 1,
            }));
    }, [objectsData]);

    // Event handlers
    const handleTimeUpdate = useCallback((time: number) => {
        setCurrentVideoTime(time);
    }, []);

    const handleMetadataLoaded = useCallback((metadata: VideoMetadata) => {
        setVideoMetadata(metadata);
    }, []);

    const handleScaledDimensionsChange = useCallback((dimensions: ScaledDimensions) => {
        setScaledDimensions(dimensions);
    }, []);

    const handlePlayStateChange = useCallback((playing: boolean) => {
        setIsPlaying(playing);
    }, []);

    const filename = useMemo(() => {
        const timeStr = formatDate(toTemporalZonedDateTime(video.time, "UTC"), "YYYYMMDDTHHmm");
        return `${site.name}_${video.device.name}_${timeStr}.mp4`;
    }, [video.device.name, video.time, site.name]);

    const handleDownload = useCallback(() => {
        if (videoBlobUrl) {
            const link = document.createElement("a");
            link.href = videoBlobUrl;
            link.download = filename || "";
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }, [videoBlobUrl, filename]);

    const handleSeekToObject = useCallback(
        (timestamp: string) => {
            const videoStartTime = new Date(video.time);
            const objectTime = new Date(timestamp);
            const seekTime = Math.max(0, (objectTime.getTime() - videoStartTime.getTime()) / 1000);
            videoRef.current?.seekToTime(seekTime);
        },
        [video.time],
    );

    // Calculate current objects for table
    const currentObjects = useMemo(() => {
        if (!objectsData.length || currentVideoTime === undefined) return [];
        const videoStartTime = new Date(video.time);
        return objectsData.filter((obj) => {
            const startTime = (new Date(obj.firstSeen).getTime() - videoStartTime.getTime()) / 1000;
            const endTime = (new Date(obj.lastSeen).getTime() - videoStartTime.getTime()) / 1000;
            return currentVideoTime >= startTime && currentVideoTime <= endTime;
        });
    }, [objectsData, currentVideoTime, video.time]);

    // Create a Set of current object IDs for the table
    const currentObjectIds = useMemo(() => {
        return new Set(currentObjects.map((obj) => obj.id));
    }, [currentObjects]);

    // Video player props
    const videoProps = {
        video: video,
        source: videoBlobUrl,
        enableDownload: true,
        isDownloading: isVideoLoading,
        downloadError: downloadError,
        onTimeUpdate: handleTimeUpdate,
        onMetadataLoaded: handleMetadataLoaded,
        onScaledDimensionsChange: handleScaledDimensionsChange,
        onDownload: handleDownload,
        onPlayStateChange: handlePlayStateChange,
        videoElementRef,
    };

    // Table props
    const tableProps = {
        objects: objectsWithOrdinal,
        currentObjectIds,
        isLoading: objectsLoading || objectsPending,
        fetchError: objectsError,
        videoStartTime: new Date(video.time),
        currentVideoTime,
        hideDirections: video.device.configs[0].directionality === "UNIDIRECTIONAL",
        onSeekToObject: handleSeekToObject,
        mode: tableMode,
        onModeChange: setTableMode,
    };

    return {
        deviceConfig,
        videoProps,
        tableProps,
        videoRef,
        videoMetadata,
        scaledDimensions,
        currentObjects,
        videoElementRef,
        isPlaying,
        setIsPlaying,
        tableMode,
        displayBoundingBoxes,
        toggleDisplayBoundingBoxes,
    };
};
